/**
 * deviceSettings — device-local App-settings preferences that have no backend
 * channel yet (T12.2 step 10b / 10c).
 *
 * Two concerns live here, both device-scoped (like `app-mode` / `app-theme`):
 *
 *   (1) Notification preferences (10c) — Deal alerts + Restock reminders. These
 *       persist a real choice and re-read on reload. Delivery (a service worker
 *       / web-push / scheduler that actually fires them) does NOT exist yet and
 *       is a separate backend Task; the App-settings surface shows a quiet
 *       "coming soon" affordance so the toggle is honest about not yet firing.
 *
 *   (2) Last-synced timestamp (10b) — recorded by the shell when a catch-up pull
 *       completes, so the Offline status row can show a real "Last synced" time
 *       instead of a fake value. Offline-first is always on (not a toggle).
 *
 * Persistence is `localStorage`; helpers are best-effort and SSR-safe.
 */

const NOTIFICATION_PREFS_KEY = 'app-notification-prefs';
const LAST_SYNCED_KEY = 'app-last-synced-at';

export interface NotificationPrefs {
  /** "When something you buy goes on sale." */
  dealAlerts: boolean;
  /** "Reminders to restock the things you run low on." */
  restockReminders: boolean;
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  dealAlerts: false,
  restockReminders: false,
};

function isNotificationPrefs(value: unknown): value is NotificationPrefs {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).dealAlerts === 'boolean' &&
    typeof (value as Record<string, unknown>).restockReminders === 'boolean'
  );
}

/** Read persisted notification prefs, falling back to the all-off default. */
export function readNotificationPrefs(): NotificationPrefs {
  try {
    const raw = window.localStorage.getItem(NOTIFICATION_PREFS_KEY);
    if (!raw) return { ...DEFAULT_NOTIFICATION_PREFS };
    const parsed: unknown = JSON.parse(raw);
    return isNotificationPrefs(parsed) ? parsed : { ...DEFAULT_NOTIFICATION_PREFS };
  } catch {
    return { ...DEFAULT_NOTIFICATION_PREFS };
  }
}

/** Persist notification prefs. Best-effort. */
export function persistNotificationPrefs(prefs: NotificationPrefs): void {
  try {
    window.localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // In-memory only this session if storage is unavailable.
  }
}

/**
 * Record that a sync just completed (called by the shell on pull→idle). Stores
 * an ISO timestamp so the Offline status row can show a real "Last synced".
 */
export function recordLastSyncedNow(): void {
  try {
    window.localStorage.setItem(LAST_SYNCED_KEY, new Date().toISOString());
  } catch {
    // Best-effort.
  }
}

/** Read the last-synced ISO timestamp, or `null` if never synced on this device. */
export function readLastSyncedAt(): string | null {
  try {
    return window.localStorage.getItem(LAST_SYNCED_KEY);
  } catch {
    return null;
  }
}

/**
 * Format an ISO last-synced timestamp into a short relative label
 * ("Just now" / "5 min ago" / "2 h ago" / a date). Pure — takes `now` so it is
 * deterministic in tests.
 */
export function formatLastSynced(iso: string | null, now: number = Date.now()): string {
  if (!iso) return 'Not yet synced';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'Not yet synced';
  const diffMs = now - then;
  if (diffMs < 0) return 'Just now';
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} d ago`;
  return new Date(then).toLocaleDateString();
}
